import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import type DocumentSettingsFormData from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/FormData/DocumentSettingsFormData';
import CheckboxTile from '@UI/Pages/Administration/DocumentGroups/DocumentGroupEdit/Shared/CheckboxTile';
import HintText from '@Guidelines/Component/HintText';
import SectionCard from '@Guidelines/Component/SectionCard';
import type DocumentTypePresetView from '@UI/Store/OrganizationWorkflows/Views/DocumentTypePresetView';
import * as React from 'react';
import { TextArea } from '@verclam/ui-kit';

type Props = {
	readonly settings: DocumentSettingsFormData;
	readonly preset: DocumentTypePresetView | null;
	/** Whether the group takes this document's type over from the file. */
	readonly isTakenOver: boolean;
	/** False only when the template is known to mark no re-evaluation section. */
	readonly isSupportedByTemplate: boolean;
	readonly onChange: React.Dispatch<React.SetStateAction<DocumentSettingsFormData>>;
};

/**
 * Whether the document also writes its "re-evaluation" part, with prompts of its own. Offered only
 * for a document the group takes over from the file — the re-evaluation rewrites that document. An
 * already enabled choice stays visible so it can be turned off.
 */
const ReevaluationSection: React.FC<Props> = ({
	settings,
	preset,
	isTakenOver,
	isSupportedByTemplate,
	onChange,
}) => {
	const handleEnabledChange = React.useCallback(
		(checked: boolean) => {
			onChange((current) => current.setReevaluationEnabled(checked));
		},
		[onChange],
	);

	const handleSystemPromptChange = React.useCallback(
		(value: string) => {
			onChange((current) => current.setReevaluationSystemPrompt(value));
		},
		[onChange],
	);

	const handleContextPromptChange = React.useCallback(
		(value: string) => {
			onChange((current) => current.setReevaluationContextPrompt(value));
		},
		[onChange],
	);

	if (!isTakenOver && !settings.reevaluationEnabled) {
		return (
			<SectionCard title="Réévaluation">
				<HintText>
					La réévaluation n’est proposée que pour un document repris automatiquement par le groupe.
				</HintText>
			</SectionCard>
		);
	}

	return (
		<SectionCard title="Réévaluation">
			<CheckboxTile
				name="reevaluationEnabled"
				label="Rédiger aussi la partie réévaluation"
				checked={settings.reevaluationEnabled}
				disabled={!isSupportedByTemplate && !settings.reevaluationEnabled}
				onChange={handleEnabledChange}
			/>
			<HintText>
				Ajoute au document une partie qui compare la situation actuelle au document repris.
			</HintText>
			<ConditionalLoader data={!isTakenOver}>
				<HintText>
					Ce type n’est plus repris automatiquement par le groupe : la réévaluation sera refusée à
					l’enregistrement.
				</HintText>
			</ConditionalLoader>
			<ConditionalLoader data={!isSupportedByTemplate}>
				<HintText>Le gabarit de ce type ne marque aucune section de réévaluation.</HintText>
			</ConditionalLoader>
			<ConditionalLoader data={settings.reevaluationEnabled}>
				<TextArea
					name="reevaluationSystemPrompt"
					label="Consignes de la réévaluation"
					rows={3}
					value={settings.reevaluationSystemPrompt ?? preset?.reevaluationSystemPrompt ?? ''}
					onChange={handleSystemPromptChange}
				/>
				<TextArea
					name="reevaluationContextPrompt"
					label="Demande de la réévaluation"
					rows={3}
					value={settings.reevaluationContextPrompt ?? preset?.reevaluationContextPrompt ?? ''}
					onChange={handleContextPromptChange}
				/>
			</ConditionalLoader>
		</SectionCard>
	);
};

export default ReevaluationSection;
